import { emit, on, storage } from '@lucid/core';

export const WISHLIST_KEY = 'lucid:wishlist';
const KEY = WISHLIST_KEY;
const MAX_ITEMS = 50;
const CARD_SECTION = 'lucid-wishlist-card';
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-_]*$/i;

const clean = (list) =>
  Array.from(new Set((Array.isArray(list) ? list : []).map((item) => String(item).trim()).filter((item) => HANDLE_PATTERN.test(item)))).slice(
    0,
    MAX_ITEMS
  );

/** The wishlist store: an array of product handles in localStorage, newest first. */
export const wishlist = {
  get() {
    return clean(storage.getJSON(KEY, []));
  },
  has(handle) {
    return this.get().includes(handle);
  },
  set(handles, { handle = null, added = null } = {}) {
    const list = clean(handles);
    storage.setJSON(KEY, list);
    emit('wishlist:change', { handles: list, handle, added });
    return list;
  },
  add(handle) {
    if (!HANDLE_PATTERN.test(handle)) return this.get();
    return this.set([handle, ...this.get().filter((item) => item !== handle)], { handle, added: true });
  },
  remove(handle) {
    return this.set(
      this.get().filter((item) => item !== handle),
      { handle, added: false }
    );
  },
  toggle(handle) {
    return this.has(handle) ? this.remove(handle) : this.add(handle);
  },
  clear() {
    return this.set([]);
  },
  count() {
    return this.get().length;
  },
  /** Share link for a list of handles: the wishlist page with ?items=a,b,c */
  shareUrl(handles = this.get(), pageUrl = window.location.pathname) {
    const url = new URL(pageUrl, window.location.origin);
    url.search = '';
    if (handles.length) url.searchParams.set('items', handles.join(','));
    return url.toString();
  },
};

window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  emit('wishlist:change', { handles: wishlist.get(), handle: null, added: null });
});

const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

/**
 * Fetches server-rendered product cards (sections/lucid-wishlist-card.liquid) through the Section Rendering API.
 * Resolves to [{ handle, html }] in the order of the handles; products that no longer exist are left out.
 */
export async function fetchCards(handles, { section = CARD_SECTION, signal } = {}) {
  const results = await Promise.all(
    handles.map(async (handle) => {
      try {
        const response = await fetch(`${root()}products/${encodeURIComponent(handle)}?section_id=${section}`, {
          signal,
          credentials: 'same-origin',
        });
        if (!response.ok) return null;
        const html = await response.text();
        const template = document.createElement('template');
        template.innerHTML = html;
        const card = template.content.querySelector('[data-lucid-wishlist-item]');
        return card ? { handle, html: card.outerHTML } : null;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        return null;
      }
    })
  );
  return results.filter(Boolean);
}

class LucidWishlistButton extends HTMLElement {
  #off = null;
  #observer = new MutationObserver(() => this.#sync());

  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.#onClick);
    this.#off = on('wishlist:change', () => this.#sync());
    this.#sync();
    this.#observer.observe(this.button, { attributes: true, attributeFilter: ['aria-pressed'] });
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    this.#off?.();
    this.#observer.disconnect();
  }

  get handle() {
    return this.dataset.handle || '';
  }

  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    wishlist.toggle(this.handle);
  };

  /** Only writes aria-pressed when it differs, so the attribute observer (which restores the state after a morph)
   * never feeds itself. */
  #sync() {
    const state = String(wishlist.has(this.handle));
    if (this.button.getAttribute('aria-pressed') === state) return;
    this.button.setAttribute('aria-pressed', state);
  }
}

class LucidWishlistCount extends HTMLElement {
  #off = null;

  connectedCallback() {
    this.#off = on('wishlist:change', () => this.#render());
    this.#render();
  }

  disconnectedCallback() {
    this.#off?.();
  }

  #render() {
    const count = wishlist.count();
    const target = this.querySelector('[data-count]') || this;
    target.textContent = count > 0 ? String(count) : '';
    this.dataset.count = String(count);
    if (this.hasAttribute('hide-empty')) this.hidden = count === 0;
    const template = this.dataset.label;
    const control = this.closest('a, button');
    if (!template || !control) return;
    control.dataset.baseLabel ??= control.getAttribute('aria-label') || '';
    const label = count > 0 ? template.replace('[count]', String(count)) : control.dataset.baseLabel;
    if (label) control.setAttribute('aria-label', label);
  }
}

/** Wishlist page: renders the stored list, or a shared list from ?items=, with share and empty states. */
class LucidWishlistGrid extends HTMLElement {
  #off = null;
  #abort = null;
  #shared = null;

  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.toolbar = this.querySelector('[data-wishlist-toolbar]');
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.sharedNote = this.querySelector('[data-wishlist-shared]');
    this.status = this.querySelector('[data-wishlist-status]');

    const params = new URLSearchParams(window.location.search);
    const items = params.get('items');
    if (items !== null) this.#shared = clean(items.split(','));

    this.addEventListener('click', this.#onClick);
    this.#off = on('wishlist:change', (event) => this.#onChange(event));
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#off?.();
    this.#abort?.abort();
  }

  get handles() {
    return this.#shared ?? wishlist.get();
  }

  #onChange(event) {
    if (this.#shared) return;
    const { handle, added } = event.detail || {};
    if (handle && added === false) {
      const item = this.grid?.querySelector(`[data-lucid-wishlist-item][data-handle="${CSS.escape(handle)}"]`);
      item?.remove();
      this.#updateCount();
      return;
    }
    this.#render();
  }

  async #render() {
    const handles = this.handles;
    this.#abort?.abort();
    this.#abort = new AbortController();
    if (this.sharedNote) this.sharedNote.hidden = !this.#shared;

    if (!handles.length) {
      this.removeAttribute('aria-busy');
      this.grid.replaceChildren();
      this.#updateCount();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    try {
      const cards = await fetchCards(handles, { signal: this.#abort.signal });
      const template = document.createElement('template');
      template.innerHTML = cards.map((card) => card.html).join('');
      this.grid.replaceChildren(template.content);
    } catch (error) {
      if (error?.name === 'AbortError') return;
    } finally {
      this.removeAttribute('aria-busy');
    }
    this.#updateCount();
  }

  #updateCount() {
    const count = this.grid.querySelectorAll('[data-lucid-wishlist-item]').length;
    this.empty.hidden = count > 0;
    if (this.toolbar) this.toolbar.hidden = count === 0;
    if (this.countEl) this.countEl.textContent = String(count);
    this.dataset.count = String(count);
  }

  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-wishlist-action]') : null;
    if (!target) return;
    const action = target.getAttribute('data-wishlist-action');

    if (action === 'share') {
      event.preventDefault();
      const url = wishlist.shareUrl(this.handles);
      const title = target.dataset.shareTitle || document.title;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
          return;
        }
        await navigator.clipboard.writeText(url);
        this.#announce(target.dataset.copied);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        const field = this.querySelector('[data-wishlist-share-url]');
        if (field instanceof HTMLInputElement) {
          field.value = url;
          field.hidden = false;
          field.focus();
          field.select();
        }
      }
    }

    if (action === 'save-shared' && this.#shared) {
      event.preventDefault();
      wishlist.set([...this.#shared, ...wishlist.get()]);
      this.#shared = null;
      const url = new URL(window.location.href);
      url.searchParams.delete('items');
      history.replaceState(history.state, '', url.toString());
      this.#announce(target.dataset.done);
      this.#render();
    }

    if (action === 'clear') {
      event.preventDefault();
      if (this.#shared) return;
      wishlist.clear();
    }
  };

  #announce(message) {
    if (!this.status || !message) return;
    this.status.textContent = message;
    window.setTimeout(() => {
      if (this.status) this.status.textContent = '';
    }, 3000);
  }
}

if (!customElements.get('lucid-wishlist-button')) customElements.define('lucid-wishlist-button', LucidWishlistButton);
if (!customElements.get('lucid-wishlist-count')) customElements.define('lucid-wishlist-count', LucidWishlistCount);
if (!customElements.get('lucid-wishlist-grid')) customElements.define('lucid-wishlist-grid', LucidWishlistGrid);

window.LucidWishlist = Object.freeze({ ...wishlist, fetchCards });
